import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import KPICard from "@/components/KPICard";
import DashboardAnalytics from "@/components/DashboardAnalytics";
import PredictiveInsights from "@/components/PredictiveInsights";
import TicketThread from "@/components/TicketThread";
import MyDay from "@/components/MyDay";
import TeamScores from "@/components/TeamScores";
import { Building2, Users2, ShieldCheck, AlertTriangle, ScrollText, Sparkles, Bell, Archive } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TID } from "@/constants/testIds";
import { formatDateTime, STATUS_LABEL } from "@/lib/format";
import { getPerms } from "@/lib/permissions";

const ARCHIVED_STATUSES = ["resolved", "closed"];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, company } = useAuth();
  const perms = getPerms(user?.role);
  const [kpis, setKpis] = useState(null);
  const [recent, setRecent] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [openTicketId, setOpenTicketId] = useState(null);

  const loadTickets = async () => {
    if (!perms.recentSupport) return;
    try {
      const t = await api.get("/tickets");
      setRecent(Array.isArray(t.data) ? t.data : []);
    } catch (err) {
      console.error("tickets failed", err);
    }
  };

  useEffect(() => {
    (async () => {
      if (perms.kpiCards) {
        try {
          const k = await api.get("/dashboard/kpis");
          setKpis(k.data);
        } catch (err) {
          console.error("dashboard/kpis failed", err);
        }
      }
      if (perms.planCard) {
        try {
          const s = await api.get("/my-subscription");
          setSubscription(s.data);
        } catch (err) {
          console.error("my-subscription failed", err);
        }
      }
      loadTickets();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const unreadTickets = useMemo(
    () => recent.filter((t) => (t.unread_by_tenant || 0) > 0),
    [recent]
  );

  // FASE 17.9 — only the last 3 non-archived tickets on the dashboard
  const activeTickets = useMemo(
    () => recent.filter((t) => !ARCHIVED_STATUSES.includes(t.status)).slice(0, 3),
    [recent]
  );

  return (
    <Layout
      title={`Olá, ${user?.name?.split(" ")[0] || "operador"}`}
      subtitle={
        company
          ? perms.planCard
            ? `${company.trade_name || company.legal_name} · Status ${STATUS_LABEL[company.status] || company.status}`
            : company.trade_name || company.legal_name
          : "Conta global · Super Admin"
      }
    >
      <div data-testid={TID.dashboard} className="space-y-6">
        {perms.kpiCards && (
          <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              label="Unidades"
              value={kpis?.total_units ?? "—"}
              hint="Operação multi-tenant"
              icon={Building2}
              testId={TID.kpiUnits}
            />
            <KPICard
              label="Usuários ativos"
              value={kpis?.total_users ?? "—"}
              hint="Times conectados"
              icon={Users2}
              testId={TID.kpiUsers}
            />
            <KPICard
              label="Conformidade"
              value={`${(kpis?.compliance_rate ?? 0).toFixed(0)}%`}
              hint="Média dos checklists"
              icon={ShieldCheck}
              tone="success"
              testId={TID.kpiCompliance}
            />
            <KPICard
              label="Ocorrências abertas"
              value={kpis?.open_occurrences ?? 0}
              hint="Não conformidades"
              icon={AlertTriangle}
              tone={kpis?.open_occurrences ? "danger" : "default"}
              testId={TID.kpiOccurrences}
            />
          </section>
        )}

        {perms.planCard && (
          <section>
            <Card className="p-6 rounded-xl border-stone-200 bg-white">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
                    Plano atual
                  </div>
                  <div className="font-display text-2xl font-bold tracking-tight mt-2">
                    {subscription?.plan?.name || "—"}
                  </div>
                  <div className="font-body text-sm text-stone-600 mt-1 flex flex-wrap items-center gap-1.5">
                    <span>
                      {subscription?.subscription?.billing_cycle === "yearly" ? "Faturamento anual" : "Faturamento mensal"}
                    </span>
                    <span aria-hidden="true">·</span>
                    <Badge variant="outline" className="align-middle">
                      {STATUS_LABEL[subscription?.subscription?.status] || subscription?.subscription?.status || "—"}
                    </Badge>
                  </div>
                </div>
                <Link to="/pricing">
                  <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="dashboard-upgrade">
                    <Sparkles className="h-4 w-4 mr-2" /> Upgrade
                  </Button>
                </Link>
              </div>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm font-body">
                {[
                  { key: "users", label: "Usuários" },
                  { key: "units", label: "Unidades" },
                  { key: "checklists", label: "Checklists" },
                ].map((it) => {
                  const used = subscription?.usage?.[it.key] ?? 0;
                  const limit = subscription?.plan?.limits?.[it.key];
                  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                  const tone = !limit
                    ? "bg-stone-300"
                    : used >= limit
                    ? "bg-rose-500"
                    : pct >= 80
                    ? "bg-amber-500"
                    : "bg-emerald-600";
                  return (
                    <div
                      key={it.key}
                      data-testid={`plan-usage-${it.key}`}
                      className="cursor-pointer rounded-lg -m-2 p-2 hover:bg-stone-50 transition-colors"
                      onClick={() => navigate("/billing")}
                      title="Ver planos e fazer upgrade"
                    >
                      <div className="text-stone-500 text-xs">{it.label}</div>
                      <div className="font-semibold text-stone-950 text-lg">
                        {used}
                        <span className="text-stone-400 text-sm font-normal"> de {limit ?? "—"}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-stone-200 overflow-hidden mt-1.5">
                        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                      {limit != null && used >= limit && (
                        <div className="text-[10px] text-rose-600 font-semibold mt-1">
                          Limite atingido · fazer upgrade →
                        </div>
                      )}
                    </div>
                  );
                })}
                <div>
                  <div className="text-stone-500 text-xs">Storage</div>
                  <div className="font-semibold text-stone-950 text-lg">{Math.round((subscription?.plan?.limits?.storage_mb ?? 0) / 1024)} GB</div>
                </div>
              </div>
            </Card>
          </section>
        )}

        {perms.myDay && <MyDay />}

        {/* FASE 19 — team punctuality qualification for managers/admins */}
        {["super_admin", "company_admin", "manager", "supervisor"].includes(user?.role) &&
          user?.company_id && <TeamScores />}

        {perms.analytics && <DashboardAnalytics hideTopUnits={!perms.topUnits} />}

        {perms.predictive && <PredictiveInsights />}

        {/* Unread ticket responses card (16.6) — admin only */}
        {perms.recentSupport && unreadTickets.length > 0 && (
          <section>
            <Card className="p-5 rounded-xl border-amber-300 bg-amber-50/60" data-testid="dashboard-tickets-unread-card">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-md bg-amber-200/70 text-amber-900 flex items-center justify-center shrink-0">
                  <Bell className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold tracking-tight">
                    Você tem {unreadTickets.length} resposta(s) nova(s) do suporte
                  </h3>
                  <div className="mt-3 space-y-2">
                    {unreadTickets.slice(0, 4).map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setOpenTicketId(t.id)}
                        className="w-full text-left rounded-md bg-white border border-amber-200 px-3 py-2 hover:border-amber-400 transition-colors"
                        data-testid={`dashboard-unread-ticket-${t.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="font-display font-semibold text-sm truncate flex-1">
                            {t.subject}
                          </div>
                          <Badge className="bg-rose-600 text-white text-[10px] border-none shrink-0">
                            {t.unread_by_tenant}
                          </Badge>
                        </div>
                        <div className="text-xs text-stone-500 font-body mt-0.5">
                          Última atividade: {formatDateTime(t.last_activity_at || t.updated_at || t.created_at)}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </section>
        )}

        {perms.recentSupport && (
          <section>
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800 font-body">
                  Suporte recente
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight">Tickets da sua operação</h3>
              </div>
              <div className="flex items-center gap-4">
                <Link to="/support" className="text-sm font-body text-emerald-800 hover:underline" data-testid="dashboard-open-support">
                  Abrir suporte
                </Link>
                <Link
                  to="/support?tab=archived"
                  className="text-sm font-body text-stone-500 hover:text-stone-800 hover:underline inline-flex items-center gap-1"
                  data-testid="dashboard-archived-tickets"
                >
                  <Archive className="h-3.5 w-3.5" /> Arquivados
                </Link>
              </div>
            </div>
            <Card className="rounded-xl border-stone-200 bg-white divide-y divide-stone-200">
              {activeTickets.length === 0 && (
                <div className="p-6 text-center text-sm text-stone-500 font-body flex items-center justify-center gap-2">
                  <ScrollText className="h-4 w-4" /> Nenhum ticket aberto.
                </div>
              )}
              {activeTickets.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setOpenTicketId(t.id)}
                  className="p-4 w-full flex items-start gap-4 hover:bg-stone-50 text-left"
                  data-testid={`dashboard-ticket-${t.id}`}
                >
                  <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0">
                    <ScrollText className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-stone-950 truncate flex items-center gap-2">
                      {t.subject}
                      {(t.unread_by_tenant || 0) > 0 && (
                        <Badge className="bg-rose-600 text-white text-[10px] border-none">
                          {t.unread_by_tenant}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-stone-500 font-body">
                      {formatDateTime(t.last_activity_at || t.updated_at || t.created_at)}
                    </div>
                  </div>
                  <Badge variant="outline" className="border-stone-300 shrink-0">{STATUS_LABEL[t.status] || t.status}</Badge>
                </button>
              ))}
            </Card>
          </section>
        )}
      </div>

      <TicketThread
        ticketId={openTicketId}
        open={!!openTicketId}
        onOpenChange={(o) => !o && setOpenTicketId(null)}
        onUpdated={loadTickets}
        viewerRole="tenant"
      />
    </Layout>
  );
}
